import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildLibraryItemPath } from '@shared/config';
import type { ModuleSummary } from '@shared/types';
import { toast } from '@shared/ui';
import { DeleteModuleConfirm } from './DeleteModuleConfirm';
import './ModuleDetail.css';

interface ModuleMenuProps {
  module: ModuleSummary;
}

const DotsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="5.5" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="12" cy="18.5" r="1.6" />
  </svg>
);

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12" />
    <path d="M7 8l5-5 5 5" />
    <path d="M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7h16" />
    <path d="M9.5 7V4.5h5V7" />
    <path d="M6.5 7l.8 12.2a1.5 1.5 0 0 0 1.5 1.3h6.4a1.5 1.5 0 0 0 1.5-1.3L17.5 7" />
  </svg>
);

/**
 * Меню «⋯» страницы модуля (desktop и mobile): «Поделиться» — копирует ссылку
 * на публичную страницу, у приватного модуля неактивно с подсказкой; «Удалить
 * модуль» — через общий DeleteModuleConfirm. Закрывается кликом вне и по Esc
 * (Esc гасим на capture, чтобы не задеть модалку подтверждения).
 */
export function ModuleMenu({ module: m }: ModuleMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const copiedTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const handleShare = async () => {
    if (!m.isPublic) return; // приватный модуль — делиться нечем, пункт неактивен
    const url = `${window.location.origin}${buildLibraryItemPath(m.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => {
        setCopied(false);
        setOpen(false);
      }, 1400);
    } catch {
      setOpen(false);
      toast({ tone: 'error', message: t('moduleDetail.share.toastError') });
    }
  };

  const openConfirm = () => {
    setOpen(false);
    setConfirmOpen(true);
  };

  return (
    <div ref={rootRef} className="md-menu-root">
      <button
        type="button"
        className="md-kebab"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('moduleDetail.menu.aria')}
        onClick={() => setOpen((v) => !v)}
      >
        <DotsIcon />
      </button>

      {open && (
        <div role="menu" className="md-menu">
          <button
            type="button"
            role="menuitem"
            className="md-menu-item"
            aria-disabled={!m.isPublic || undefined}
            onClick={handleShare}
          >
            <ShareIcon />
            <span>
              <span aria-live="polite">{copied ? t('moduleDetail.share.done') : t('moduleDetail.share.cta')}</span>
              {!m.isPublic && <span className="md-menu-sub">{t('moduleDetail.share.privateBlocked')}</span>}
            </span>
          </button>
          <div className="md-menu-sep" role="separator" />
          <button type="button" role="menuitem" className="md-menu-item md-menu-item--danger" onClick={openConfirm}>
            <TrashIcon />
            {t('moduleDetail.menu.delete')}
          </button>
        </div>
      )}

      <DeleteModuleConfirm module={m} open={confirmOpen} onClose={() => setConfirmOpen(false)} />
    </div>
  );
}
